import { useState, useMemo } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GameQueryKeys } from '@/modules/game/keys'
import useMutationBatchUpdateGamePreset from '@/modules/game/api/hooks/useMutationBatchUpdateGamePreset'
import { Label } from '@/common/ui/label'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import { RtpPresetPrecedenceTooltip } from '@/common/components/RtpPresetPrecedenceTooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Checkbox } from '@/common/ui/checkbox'
import { Badge } from '@/common/ui/badge'
import { Settings2Icon, ChevronDown, SearchIcon } from 'lucide-react'
import {
	getProviderPresetType,
	isRtpSupportedProvider,
	PresetType,
} from '@/modules/game/utils'
import { GameProviderListTableData } from '@/modules/game/type'

interface BatchAssignGameRtpPresetProps {
	providers: GameProviderListTableData[]
}

export default function BatchAssignGameRtpPreset({
	providers,
}: BatchAssignGameRtpPresetProps) {
	const [isOpen, setIsOpen] = useState(false)
	const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([])
	const [presetValue, setPresetValue] = useState('')
	const [presetNumber, setPresetNumber] = useState('')
	const [clearPresetNumber, setClearPresetNumber] = useState(false)
	const [providerSearch, setProviderSearch] = useState('')
	const { mutateAsync: batchUpdate, isPending } =
		useMutationBatchUpdateGamePreset()
	const queryClient = useQueryClient()
	const { data: presets } = useQueryGetPresetId()

	const rtpProviders = useMemo(
		() => providers.filter((p) => isRtpSupportedProvider(p.name)),
		[providers],
	)

	const filteredProviders = useMemo(() => {
		if (!providerSearch) return rtpProviders
		return rtpProviders.filter((p) =>
			p.name.toLowerCase().includes(providerSearch.toLowerCase()),
		)
	}, [rtpProviders, providerSearch])

	const hasPresetIdProviders = selectedProviderIds.some(
		(id) =>
			getProviderPresetType(providers.find((p) => p.id === id)?.name || '') ===
			PresetType.PresetId,
	)

	const hasPresetNumberProviders = selectedProviderIds.some(
		(id) =>
			getProviderPresetType(providers.find((p) => p.id === id)?.name || '') ===
			PresetType.PresetNumber,
	)

	const handleProviderToggle = (providerId: string) => {
		setSelectedProviderIds((prev) =>
			prev.includes(providerId)
				? prev.filter((id) => id !== providerId)
				: [...prev, providerId],
		)
	}

	const handleClearAll = () => {
		setSelectedProviderIds([])
	}

	const handleBatchUpdate = async () => {
		try {
			const payload: {
				providerIds: string[]
				defaultPreset?: string | null
				defaultPresetNumber?: number | null
			} = { providerIds: selectedProviderIds }

			if (presetValue === '__clear__') {
				payload.defaultPreset = null
			} else if (presetValue) {
				payload.defaultPreset = presetValue
			}

			if (clearPresetNumber) {
				payload.defaultPresetNumber = null
			} else if (presetNumber) {
				payload.defaultPresetNumber = Number(presetNumber)
			}

			const result = await batchUpdate(payload)
			queryClient.invalidateQueries({
				queryKey: [GameQueryKeys.GetGameList],
			})

			if (result.skippedCount > 0) {
				toast.warning(
					`อัพเดทแล้ว ${result.updatedCount} เกม ข้าม ${result.skippedCount} รายการ`,
				)
			} else {
				toast.success(`อัพเดทแล้ว ${result.updatedCount} เกม`)
			}
			setIsOpen(false)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const handleClose = () => {
		setIsOpen(false)
		setSelectedProviderIds([])
		setPresetValue('')
		setPresetNumber('')
		setClearPresetNumber(false)
		setProviderSearch('')
	}

	const selectedCount = selectedProviderIds.length
	const canSubmit =
		selectedCount > 0 &&
		(presetValue !== '' || clearPresetNumber || presetNumber !== '')
	const selectedNames = selectedProviderIds
		.map((id) => providers.find((p) => p.id === id)?.name)
		.filter(Boolean)

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				onClick={() => setIsOpen(true)}
				className="gap-2 sm:h-9"
			>
				<Settings2Icon className="h-4 w-4" />
				เปลี่ยน preset สำหรับทุกเกม
			</Button>

			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							ตั้งค่า Game RTP Preset ตามผู้ให้บริการ
							<RtpPresetPrecedenceTooltip highlightLevel={5} />
						</DialogTitle>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>เลือกผู้ให้บริการ</Label>
							<Popover
								onOpenChange={(open) => {
									if (!open) setProviderSearch('')
								}}
							>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										role="combobox"
										className="w-full justify-between"
									>
										<span className="truncate">
											{selectedCount > 0
												? `${selectedCount} ผู้ให้บริการที่เลือก`
												: 'เลือกผู้ให้บริการเกม'}
										</span>
										<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-full p-0" align="start">
									<div className="space-y-2 p-2">
										<div className="flex items-center border-b px-2">
											<SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
											<Input
												placeholder="ค้นหาผู้ให้บริการ..."
												className="h-8 border-0 p-0 text-sm focus-visible:ring-0"
												value={providerSearch}
												onChange={(e) => setProviderSearch(e.target.value)}
											/>
										</div>
										<div className="max-h-[200px] space-y-1 overflow-y-auto">
											{filteredProviders.map((provider) => (
												<div
													key={provider.id}
													className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
													onClick={() => handleProviderToggle(provider.id)}
												>
													<Checkbox
														checked={selectedProviderIds.includes(provider.id)}
														className="pointer-events-none h-4 w-4"
													/>
													<span className="text-sm">
														{provider.displayName || provider.name}
													</span>
												</div>
											))}
											{filteredProviders.length === 0 && (
												<div className="py-2 text-center text-sm text-muted-foreground">
													ไม่พบผู้ให้บริการ
												</div>
											)}
										</div>
										{selectedCount > 0 && (
											<Button
												variant="ghost"
												size="sm"
												className="w-full justify-center text-xs"
												onClick={handleClearAll}
											>
												ล้างทั้งหมด
											</Button>
										)}
									</div>
								</PopoverContent>
							</Popover>
							{selectedNames.length > 0 && (
								<div className="flex flex-wrap gap-1">
									{selectedNames.map((name) => (
										<Badge
											key={name}
											variant="secondary"
											className="rounded-sm px-2 py-1 text-xs font-normal"
										>
											{name}
										</Badge>
									))}
								</div>
							)}
						</div>

						{hasPresetIdProviders && (
							<div className="grid gap-2">
								<Label>เลือก RTP Preset (สำหรับ PGHARD, JOKERHARD)</Label>
								<Select
									value={presetValue}
									onValueChange={setPresetValue}
									disabled={isPending}
								>
									<SelectTrigger>
										<SelectValue placeholder="ไม่เปลี่ยน" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="">ไม่เปลี่ยน</SelectItem>
										<SelectItem value="__clear__">
											ล้างค่า RTP Preset
										</SelectItem>
										{presets?.presets.map((preset) => (
											<SelectItem key={preset.id} value={preset.id}>
												{preset.note}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}

						{hasPresetNumberProviders && (
							<div className="grid gap-2">
								<Label>ตั้งค่าเปอร์เซ็น RTP 30-99 (สำหรับ HARDV2*)</Label>
								<Input
									type="number"
									min={30}
									max={99}
									placeholder="ไม่เปลี่ยน"
									value={presetNumber}
									onChange={(e) => setPresetNumber(e.target.value)}
									disabled={isPending || clearPresetNumber}
								/>
								<div className="flex items-center gap-2">
									<Checkbox
										id="clear-game-preset-number"
										checked={clearPresetNumber}
										onCheckedChange={(checked) => {
											setClearPresetNumber(!!checked)
											if (checked) setPresetNumber('')
										}}
										disabled={isPending}
									/>
									<Label
										htmlFor="clear-game-preset-number"
										className="cursor-pointer text-sm"
									>
										ล้างค่า RTP เปอร์เซ็น
									</Label>
								</div>
							</div>
						)}

						{selectedCount > 0 && (
							<p className="text-sm text-muted-foreground">
								กำลังจะอัพเดท RTP ให้ทุกเกมภายใต้ {selectedCount}{' '}
								ผู้ให้บริการที่เลือก
							</p>
						)}
					</div>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose}>
							ยกเลิก
						</Button>
						<Button
							onClick={handleBatchUpdate}
							disabled={isPending || !canSubmit}
						>
							{isPending ? 'กำลังบันทึก...' : 'บันทึก Game RTP'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
